import moment from 'moment';
import { ValidateResult } from 'react-hook-form';
import { parse as parseDomain } from 'tldts';
import { z } from 'zod';
import { Editable, ChangeType } from 'types';

export const zipCodeValidation = /^\d{5}$/;
export const ssnValidation = /^\d{4}$/;
export const phoneNumberValidation = /^(\+1)?(\d{10})$/; // /^[+]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
export const dateValidation = /^\d{2}\/\d{2}\/\d{4}$/;
export const timeValidation = /^([0-1][0-9]|[2][0-3]):([0-5][0-9]) (AM|PM)$/;
export const webURLValidation =
  // eslint-disable-next-line max-len
  /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/;

export const validations = {
  zipCodeValidation,
  ssnValidation,
  phoneNumberValidation,
  dateValidation,
  timeValidation,
  webURLValidation,
};

const emailSchema = z.string().email();

// Mirrors the API's `isValidEmailAddress` (api/src/utils): the value must match
// zod's email shape AND resolve to an ICANN-recognized top-level domain via the
// Public Suffix List (tldts). This fails typo TLDs like `icloud.con` in the UI
// too — Avigilon rejects those and they silently break the worker's badge sync.
export const isValidEmailAddress = (email: string): boolean => {
  if (email == null) return false;
  const trimmed = email.trim();
  if (!emailSchema.safeParse(trimmed).success) return false;
  const domain = trimmed.slice(trimmed.indexOf('@') + 1);
  return parseDomain(domain).isIcann === true;
};

function removeTrailingNotes(text: string): string {
  return text?.replace(/\s*\((optional|required)\)$/i, '');
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals
export function InvalidField(
  strings: TemplateStringsArray,
  fieldAsVariable?: string,
): string {
  const field = removeTrailingNotes(fieldAsVariable ?? strings?.[0]) ?? 'field';
  return `Please enter a valid ${field}`;
}

export function RequiredField(
  strings: TemplateStringsArray,
  fieldAsVariable?: string,
): string {
  const field = removeTrailingNotes(fieldAsVariable ?? strings?.[0]) ?? 'Field';
  return `${field} is required`;
}

// react-hook-form `validate` rule for an email field. Empty values pass so the
// separate `required` rule owns emptiness; pass `message` to override the
// default invalid-email copy.
export const emailAddressValidation =
  (message?: string) =>
  (value: string): ValidateResult =>
    !value || isValidEmailAddress(value) || (message ?? InvalidField`email`);

export const FILE_CHANGED_ERROR_MESSAGE =
  'It looks like the file you were uploading was modified or deleted after selection. Please upload the file again.';

export const ageValidation =
  (fieldName: string, minAge: number, maxAge: number) =>
  (date: string): ValidateResult => {
    if (date) {
      const age = Math.max(moment().diff(date, 'years'), 0);
      return age < minAge || maxAge < age ? InvalidField`${fieldName}` : true;
    }
    return true;
  };

export const editableArrayRequiredValidation =
  <T>(
    fieldName: string,
    isRequiredSpec:
      | boolean
      | ((data: Editable<T>[], formValues: any) => boolean),
  ) =>
  (data: Editable<T>[], formValues: any): ValidateResult => {
    const isRequired =
      typeof isRequiredSpec === 'function'
        ? isRequiredSpec(data, formValues)
        : isRequiredSpec;
    if (
      isRequired &&
      !data?.some((item) => item.changeType !== ChangeType.Removed)
    ) {
      return RequiredField`${fieldName}`;
    }
    return true;
  };

export const editableRequiredValidation =
  (fieldName: string, isRequiredSpec: boolean | (() => boolean)) =>
  (data: Editable<unknown>): ValidateResult => {
    const isRequired =
      typeof isRequiredSpec === 'function' ? isRequiredSpec() : isRequiredSpec;

    if (isRequired && (!data || data.changeType === ChangeType.Removed)) {
      return RequiredField`${fieldName}`;
    }
    return true;
  };

export const startTimeIsBeforeEndTimeValidation = (
  value: moment.MomentInput,
  formValues: { endAt: moment.MomentInput },
): ValidateResult => {
  const startAtMoment = moment(value, 'hh:mm A');
  const { endAt } = formValues;
  const endAtMoment = moment(endAt, 'hh:mm A');
  if (
    startAtMoment.isValid() &&
    endAtMoment.isValid() &&
    startAtMoment.isAfter(endAtMoment)
  ) {
    return 'The start time must be before the end time.';
  }
  return null;
};
